/**
 * A singleton utility class for handling SPA route navigation using the browser's History API.
 * Supports base URL registration per app instance and emits custom navigation events.
 */
class WangsUIRouter {
    static SINGLETON_KEY = '__WANGS_UI_ROUTER_INSTANCE__';
    constructor() {
        // Return existing instance if already created
        if (globalThis[WangsUIRouter.SINGLETON_KEY]) {
            return globalThis[WangsUIRouter.SINGLETON_KEY];
        }
        // Save instance to globalThis
        globalThis[WangsUIRouter.SINGLETON_KEY] = this;
    }
    /**
     * Stores base URLs associated with different app identifiers.
     */
    _baseUrl = '';
    /**
     * Registers a base URL for a specific app.
     *
     * @param base - The base URL to be used as a prefix for all routes in that app.
     */
    setNavigationBaseUrl(base) {
        this._baseUrl = this.normalizeBase(base);
    }
    /**
     * Retrieves the base URL for a specific app.
     *
     * @returns The base URL registered for the app or an empty string if not set.
     */
    getNavigationBaseUrl() {
        return this._baseUrl;
    }
    /**
     * Ensures the base URL ends with a single slash and removes trailing slashes.
     *
     * @param base - The base URL to normalize.
     * @returns A clean base URL ending with a single `/`.
     */
    normalizeBase(base) {
        return base.replace(/\/+$/, '').concat('/');
    }
    /**
     * Prepends the base URL (if any) to the given path.
     *
     * @param path - The relative path to normalize.
     * @returns A fully normalized path prefixed with the base URL if applicable.
     */
    normalizePath(path) {
        if (!path.startsWith('/')) {
            // The path must be relative to current location path
            // E.g., path = 'module', location '/base/currentpath'
            // return '/base/module'
            return new URL(path, window.location.href).pathname;
        }
        const base = this.getNavigationBaseUrl();
        if (!base)
            return path;
        const sanitizedPath = path.slice(1);
        return `${base}${sanitizedPath}`;
    }
    /**
     * Removes the specified base path from the beginning of a given route path.
     *
     * This is useful when your app is deployed under a subdirectory (e.g., `/base`)
     * and you want to normalize the route path to match menu or route definitions
     * that don't include the base.
     *
     * @param path - The full route path (e.g., `/base/about`).
     * @param base - The optional base URL to remove (e.g., `/base`).
     * @returns The normalized path without the base prefix (e.g., `/about`).
     *
     * @example
     * stripBase('/base/home', '/base'); // returns '/home'
     * stripBase('/about', '/base');     // returns '/about' (base not present)
     * stripBase('/base', '/base');      // returns '/'
     */
    stripBase(path, base) {
        base ||= this.getNavigationBaseUrl();
        return base !== '/' && path.startsWith(base) ? '/' + path.slice(base.length).replace(/^\/+/, '') : path;
    }
    /**
     * Pushes a new entry to the browser's history stack.
     *
     * @param route - The target URL or path to push. Can be a string, URL object, or null.
     * @param data - Optional state object associated with the history entry. Accessible via `history.state`.
     * @param unused - Optional title parameter (currently unused due to limited browser support).
     *
     * @example
     * Router.push('/about', { from: 'home' });
     */
    push(route, data = {}, unused = '') {
        const state = {
            ...data,
            ...(history.state ?? {}),
            current: route,
            back: history.state?.current,
            forward: undefined,
            replaced: false,
            position: history.length + 1,
        };
        window.history.pushState(state, unused, route);
    }
    /**
     * Replaces the current entry in the browser's history stack.
     *
     * @param route - The target URL or path to replace with. Can be a string, URL object, or null.
     * @param data - Optional state object associated with the history entry. Accessible via `history.state`.
     * @param unused - Optional title parameter (currently unused due to limited browser support).
     *
     * @example
     * Router.replace('/login', { reason: 'session expired' });
     */
    replace(route, data = {}, unused = '') {
        const state = {
            ...data,
            ...(history.state ?? {}),
            forward: undefined,
            current: route,
            replaced: true,
        };
        window.history.replaceState(state, unused, route);
    }
    /**
     * Dispatches a custom event before navigation occurs.
     * Used to notify subscribers of an upcoming route change.
     *
     * @param route - The route that will be navigated to.
     * @param mode - The navigation mode ('push' or 'replace').
     * @param data - Optional state object for the navigation.
     */
    dispatchBeforeNavigationEvent(route, mode, data) {
        window.dispatchEvent(new CustomEvent('wangs-ui-core:before-navigation-event', {
            detail: { route, mode, state: data },
        }));
    }
    /**
     * Dispatches a custom event after navigation occurs.
     * Used to notify subscribers that the route has changed.
     *
     * @param route - The route that was navigated to.
     * @param mode - The navigation mode ('push' or 'replace').
     * @param data - Optional state object for the navigation.
     */
    dispatchNavigationEvent(route, mode, data) {
        window.dispatchEvent(new CustomEvent('wangs-ui-core:navigation-event', {
            detail: { route, mode, state: data },
        }));
    }
    /**
     * Navigates to a specified route using the browser's History API.
     *
     * This updates the URL in the browser without reloading the page.
     * It can either push a new entry to the history stack or replace the current one.
     * Also dispatches custom events before and after the navigation.
     *
     * @param route - The target route to navigate to. Can be a string or URL object.
     * @param navigationMode - Determines how navigation is handled.
     *                          - `'push'` (default): Adds a new entry to the browser history.
     *                          - `'replace'`: Replaces the current history entry.
     * @param data - Optional state object associated with the history entry. Can be accessed via `history.state`.
     * @param unused - Optional title parameter (currently unused due to browser support limitations).
     *
     * @example
     * router.navigateToRoute('/about', 'push', { some: 'state' }, '',);
     */
    navigateToRoute(route, navigationMode = 'push', data = history.state, unused = '') {
        if (!route)
            return;
        const rawUrl = typeof route === 'string' ? route : route.toString();
        const finalUrl = this.normalizePath(rawUrl);
        if (finalUrl === location.pathname) {
            return console.warn('[WangsUIRouter] Navigation aborted: target route is the same as the current route.');
        }
        this.dispatchBeforeNavigationEvent(finalUrl, navigationMode, data);
        if (navigationMode === 'push') {
            this.push(finalUrl, data, unused);
        }
        else {
            this.replace(finalUrl, data, unused);
        }
        this.dispatchNavigationEvent(finalUrl, navigationMode, data);
    }
}
const router = new WangsUIRouter();

export { router as r };
